'use client';

import { useMemo } from 'react';
import {
  useWorkflowData,
  useCreateBulkDrafts,
  useSendCorrespondences
} from '@/lib/hooks/use-workflow-data';
import WorkflowDataService from '@/lib/services/correspondences/workflows/workflow-data.service';
import { transformInstitution } from '@/lib/services/correspondences/workflows/transformers';
import { toast } from '@/lib/toast';
import type {
  BackendCorrespondence,
  WorkflowInstitution
} from '@/lib/services/correspondences/workflows/workflow-types';

interface SendInstitutionData {
  id: number | string;
  letterContent?: string;
  emailSubject?: string;
  emailBody?: string;
  emailTo?: string;
  signatoryUserId?: number | null;
  /** File IDs to attach as additional documents */
  attachments?: number[];
}

export interface SendDataPayload {
  institutions: Array<Pick<WorkflowInstitution, 'id' | 'emailAddress'>>;
  sendData?: {
    institutions?: SendInstitutionData[];
    sendSenderCopy?: boolean;
  };
}

interface CorrespondenceUpdates {
  correspondenceLetterBody?: string;
  subject?: string;
  mainContent?: string;
  emailto?: string;
  signatoryUser?: { id: number } | null;
  requiresEmailResearch?: boolean;
}

/**
 * Main workflow hook that uses the workflow data service WITHOUT transformations
 * This follows the principle: "Frontend serves the backend"
 */
export function useNotificationWorkflow(matterId: number) {
  // Fetch workflow data using our service - no transformation needed!
  const {
    data: workflowData,
    isLoading,
    error,
    refetch
  } = useWorkflowData(matterId, 'notification');

  // Merge institutions from pending-research-email correspondences
  // that the backend may have filtered out of the institutions list
  const mergedInstitutions = useMemo(() => {
    if (!workflowData) return [];

    const institutions = [...workflowData.institutions];
    const existingIds = new Set(institutions.map((inst) => inst.id));
    const pendingResearchCorrespondences = (
      workflowData.correspondences || []
    ).filter(
      (c) =>
        c.type === 'notification' &&
        c.status === 'pending-research-email' &&
        c.institution &&
        !existingIds.has(c.institution.id)
    );
    for (const corr of pendingResearchCorrespondences) {
      institutions.push(transformInstitution(corr.institution, [], []));
      existingIds.add(corr.institution.id);
    }

    return institutions;
  }, [workflowData]);

  // Validate workflow data directly without transformations
  const validation = useMemo(() => {
    if (!workflowData) {
      return {
        canProceed: false,
        blockingErrors: isLoading ? ['Loading workflow data...'] : [],
        warnings: []
      };
    }

    const blockingErrors: string[] = [];
    const warnings: string[] = [];

    // Check for institutions (use merged list that includes pending-research ones)
    if (mergedInstitutions.length === 0) {
      blockingErrors.push('No institutions available for notification');
    }

    // Check for death certificate
    const hasDeathCertificate = workflowData.availableDocuments.some(
      (doc) => doc.fileType === 'death-certificate' && doc.isCertifiedCopy
    );
    if (!hasDeathCertificate) {
      warnings.push('No certified death certificate uploaded');
    }

    // Check for signatories
    if (workflowData.availableSignatories.length === 0) {
      blockingErrors.push('No signatories available');
    } else {
      const hasSignatureOnFile = workflowData.availableSignatories.some(
        (s) => s.hasSignature
      );
      if (!hasSignatureOnFile) {
        warnings.push('No signatory has a signature on file');
      }
    }

    // Check for law firm letterhead
    if (!workflowData.lawFirm?.hasLetterhead) {
      warnings.push('Law firm letterhead not configured');
    }

    return {
      canProceed: blockingErrors.length === 0,
      blockingErrors,
      warnings
    };
  }, [workflowData, mergedInstitutions, isLoading]);

  // Mutations
  const createDrafts = useCreateBulkDrafts();
  const sendCorrespondences = useSendCorrespondences(matterId);

  // Send notifications handler
  const sendNotifications = async (sendData: SendDataPayload) => {
    try {
      // Get the current correspondences (should already exist from component mount)
      const currentCorrespondences: BackendCorrespondence[] =
        workflowData?.correspondences || [];

      // Get institution IDs for filtering
      const institutionIds = sendData.institutions.map((inst) =>
        typeof inst.id === 'string' ? parseInt(inst.id) : inst.id
      );

      // Verify correspondences exist (they should have been created on component mount)
      const missingCorrespondences = institutionIds.filter(
        (instId: number) =>
          !currentCorrespondences.some(
            (c) => c.institution?.id === instId && c.type === 'notification'
          )
      );

      if (missingCorrespondences.length > 0) {
        throw new Error(
          `Correspondences not found for institutions: ${missingCorrespondences.join(', ')}`
        );
      }

      // Use the existing correspondences
      const freshCorrespondences = currentCorrespondences;

      // Step 2: Update each correspondence if user made edits (letter content, email, subject, signatory)
      if (sendData.sendData?.institutions) {
        for (const instData of sendData.sendData.institutions) {
          const instId =
            typeof instData.id === 'string'
              ? parseInt(instData.id)
              : instData.id;

          // Find the correspondence from the fresh data, not the draft response
          // Must filter by type to avoid updating the wrong correspondence
          const correspondence = freshCorrespondences.find(
            (c) => c.institution?.id === instId && c.type === 'notification'
          );
          const correspondenceId = correspondence?.id;

          if (correspondenceId) {
            const updates: CorrespondenceUpdates = {};

            if (instData.letterContent) {
              updates.correspondenceLetterBody = instData.letterContent;
            }
            if (instData.emailSubject) {
              updates.subject = instData.emailSubject;
            }
            if (instData.emailBody) {
              updates.mainContent = instData.emailBody;
            }
            if (instData.emailTo) {
              updates.emailto = instData.emailTo;
            }
            if (instData.signatoryUserId !== undefined) {
              updates.signatoryUser = instData.signatoryUserId
                ? { id: instData.signatoryUserId }
                : null;
            }

            // Flag for email research if institution has no email and user didn't provide one
            const institution = sendData.institutions.find((i) => {
              const iId = typeof i.id === 'string' ? parseInt(i.id) : i.id;
              return iId === instId;
            });
            if (institution && !institution.emailAddress && !instData.emailTo) {
              updates.requiresEmailResearch = true;
            }

            // Only call update if there are changes
            if (Object.keys(updates).length > 0) {
              // updates uses the backend update shape (CorrespondenceUpdates),
              // not the legacy CorrespondenceDraft shape the action's type
              // refers to — see workflow-types.ts. Cast through unknown to
              // bridge the two until the action's signature is migrated.
              const result = await WorkflowDataService.updateDraftAction(
                matterId,
                correspondenceId,
                updates as unknown as Parameters<
                  typeof WorkflowDataService.updateDraftAction
                >[2]
              );
              if (!result.success) {
                throw new Error(result.error || 'Failed to update draft');
              }
            }
          }
        }
      }

      // Step 3: Send all correspondences using fresh correspondence IDs
      const correspondenceIds = freshCorrespondences
        .filter((c) => {
          // Only send correspondences for the institutions we selected
          const institutionId = c.institution?.id;
          if (institutionId === undefined) return false;

          // Only send draft correspondences of notification type
          const isDraft = c.status === 'draft';
          const isNotification = c.type === 'notification';

          return (
            institutionIds.includes(institutionId) && isDraft && isNotification
          );
        })
        .map((c) => c.id)
        .filter((id): id is number => id !== undefined && id !== null);

      const sendSenderCopy = sendData.sendData?.sendSenderCopy || false;

      const results = await sendCorrespondences.mutateAsync({
        correspondenceIds,
        sendSenderCopy
      });

      // Show toast if any institutions required email research (no manual email provided)
      const hadEmailResearch = (sendData.sendData?.institutions || []).some(
        (instData) => {
          const institution = sendData.institutions.find((i) => {
            const iId = typeof i.id === 'string' ? parseInt(i.id) : i.id;
            const instId =
              typeof instData.id === 'string'
                ? parseInt(instData.id)
                : instData.id;
            return iId === instId;
          });
          return institution && !institution.emailAddress && !instData.emailTo;
        }
      );
      if (hadEmailResearch) {
        toast.info(
          'Request submitted. We will locate details and notify shortly.'
        );
      }

      return results;
    } catch (error) {
      console.error('Failed to send notifications:', error);
      throw error;
    }
  };

  // Transform send results to match component expectations
  const sendResults = useMemo(() => {
    if (!sendCorrespondences.data) return undefined;

    const { results } = sendCorrespondences.data;
    const successful = results
      .filter((r) => r.success && r.correspondenceId)
      .map((r) => r.correspondenceId!);
    const failed = results
      .filter((r) => !r.success)
      .map((r) => ({
        id: r.institutionId,
        error: r.error || 'Send failed'
      }));

    return { successful, failed };
  }, [sendCorrespondences.data]);

  // Return data directly without transformation
  return {
    data: workflowData
      ? {
          institutions: mergedInstitutions,
          documents: workflowData.availableDocuments,
          signatories: workflowData.availableSignatories,
          correspondences: workflowData.correspondences, // Include backend correspondences
          matter: workflowData.matter,
          lawFirm: workflowData.lawFirm
        }
      : undefined,
    isLoading,
    error,
    validation,
    sendNotifications,
    refetchWorkflowData: refetch, // Expose refetch for updating correspondences
    isSending: createDrafts.isPending || sendCorrespondences.isPending,
    sendError: createDrafts.error || sendCorrespondences.error,
    sendResults
  };
}

// Export individual hooks for specific use cases
export { useWorkflowData as useNotificationWorkflowData } from '@/lib/hooks/use-workflow-data';
export {
  useCreateBulkDrafts,
  useSendCorrespondences
} from '@/lib/hooks/use-workflow-data';
